import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AreaFilter from 'src/component/v2/AreaFilter';
import { Button } from 'src/component/ds/ui/button';
import { FilterSelect } from 'src/component/ds/ui/filterSelect';
import { cn } from 'src/component/ds/utils';
import {
  clearSelectedFilters,
  fetchCommodities,
  fetchLocations,
  setSelectedCommodity,
  setSelectedLocation
} from 'src/module/filter/action';
import { getClientId } from 'src/module/authentication/selector';
import {
  getCommodityNames,
  getIsAllFiltersEmpty,
  getLocationNames,
  getSelectedCommodity,
  getSelectedDeviceGroup,
  getSelectedLocation
} from 'src/module/filter/selector';
import { useTranslation } from 'src/i18n';

const SELECT_CLASSES = 'ds-w-[206px] max-ds-pair:ds-min-w-[140px] max-ds-pair:ds-flex-1';

export default function PageFilterBar ({ className }: { className?: string }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const clientId = useSelector(getClientId);
  const locations = useSelector(getLocationNames);
  const commodities = useSelector(getCommodityNames);
  const selectedLocation = useSelector(getSelectedLocation);
  const selectedCommodity = useSelector(getSelectedCommodity);
  const isAllFiltersEmpty = useSelector(getIsAllFiltersEmpty);
  const selectedDeviceGroup = useSelector(getSelectedDeviceGroup);
  const nothingToClear = isAllFiltersEmpty && !selectedDeviceGroup;

  useEffect(() => {
    dispatch(fetchLocations());
    dispatch(fetchCommodities());
  }, [dispatch, clientId]);

  return (
    <div
      className={cn(
        'ds-flex ds-flex-wrap ds-items-center ds-gap-4 ds-border-b ds-border-border ds-bg-surface ds-p-4',
        className
      )}
      data-testid='page-filter-bar'
    >
      <AreaFilter className='ds-min-w-0 ds-flex-1' />

      <div className='ds-ml-auto ds-flex ds-flex-wrap ds-items-center ds-gap-4 max-ds-pair:ds-w-full'>
        <FilterSelect
          label={t('deviceList.filterPrefix', { label: t('filters.location') })}
          value={selectedLocation}
          options={locations}
          allLabel={t('common.all')}
          onValueChange={(location) => dispatch(setSelectedLocation(location))}
          aria-label={t('deviceList.locationFilterLabel')}
          className={SELECT_CLASSES}
          testId='location-filter'
        />
        <FilterSelect
          label={t('deviceList.filterPrefix', { label: t('filters.commodity') })}
          value={selectedCommodity}
          options={commodities}
          allLabel={t('common.all')}
          onValueChange={(commodity) => dispatch(setSelectedCommodity(commodity))}
          aria-label={t('deviceList.commodityFilterLabel')}
          className={SELECT_CLASSES}
          testId='commodity-filter'
        />
        <Button
          variant='outline'
          className='ds-py-[7px] ds-shadow-xs'
          disabled={nothingToClear}
          onClick={() => dispatch(clearSelectedFilters())}
          data-testid='clear-all-filters'
        >
          {t('deviceList.clearAll')}
        </Button>
      </div>
    </div>
  );
}
